import { useState } from "react";
import { Search, Plus, Eye, Building2, ChevronDown, ChevronUp, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Buildings as PhBuildings } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { StatCard } from "@/components/StatCard";
import { Badge } from "@/components/ui/badge";
import { useUnits, useCreateUnit, useUpdateUnit, useDeleteUnit } from "@/hooks/useUnits";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export default function Units() {
  const { effectiveRole } = useAuth();
  const isIndividual = effectiveRole === "individual";
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [viewUnit, setViewUnit] = useState<any>(null);
  const [editUnit, setEditUnit] = useState<any>(null);
  const [deleteUnitId, setDeleteUnitId] = useState<string | null>(null);
  const [showOptional, setShowOptional] = useState(false);
  const [form, setForm] = useState({
    // Required fields
    unit_number: "",
    owner_name: "",
    // Contact
    owner_email: "",
    owner_phone: "",
    // Status & STR
    str_approved: false,
    status: "inactive",
    strata_hotel_category: "" as "" | "category_1" | "category_2" | "category_3",
    // Optional property details
    floor_number: "",
    bedrooms: "",
    bathrooms: "",
    sq_footage: "",
    parking_stall: "",
    locker_number: "",
    notes: "",
  });
  const { data: units = [], isLoading, error } = useUnits();
  const createUnit = useCreateUnit();
  const updateUnit = useUpdateUnit();
  const deleteUnit = useDeleteUnit();

  const filtered = units.filter(
    (u: any) =>
      u.unit_number?.toLowerCase().includes(search.toLowerCase()) ||
      u.owner_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalUnits = units.length;
  const strApproved = units.filter((u: any) => u.str_approved).length;
  const activeUnits = units.filter((u: any) => u.status === "active").length;

  const resetForm = () => {
    setForm({
      unit_number: "", owner_name: "", owner_email: "", owner_phone: "",
      str_approved: false, status: "inactive", strata_hotel_category: "",
      floor_number: "", bedrooms: "", bathrooms: "", sq_footage: "",
      parking_stall: "", locker_number: "", notes: "",
    });
    setShowOptional(false);
  };

  const openEdit = (u: any) => {
    setEditUnit(u);
    setForm({
      unit_number: u.unit_number ?? "",
      owner_name: u.owner_name ?? "",
      owner_email: u.owner_email ?? "",
      owner_phone: u.owner_phone ?? "",
      str_approved: u.str_approved ?? false,
      status: u.status ?? "inactive",
      strata_hotel_category: u.strata_hotel_category ?? "",
      floor_number: u.floor_number?.toString() ?? "",
      bedrooms: u.bedrooms?.toString() ?? "",
      bathrooms: u.bathrooms?.toString() ?? "",
      sq_footage: u.sq_footage?.toString() ?? "",
      parking_stall: u.parking_stall ?? "",
      locker_number: u.locker_number ?? "",
      notes: u.notes ?? "",
    });
    setOpen(true);
  };

  const handleSubmit = () => {
    if (!form.unit_number || !form.owner_name) return;
    const payload = {
      unit_number: form.unit_number,
      owner_name: form.owner_name,
      owner_email: form.owner_email || undefined,
      owner_phone: form.owner_phone || undefined,
      str_approved: form.str_approved,
      status: form.status as any,
      strata_hotel_category: (form.strata_hotel_category as any) || undefined,
      floor_number: form.floor_number ? parseInt(form.floor_number) : undefined,
      bedrooms: form.bedrooms ? parseInt(form.bedrooms) : undefined,
      bathrooms: form.bathrooms ? parseFloat(form.bathrooms) : undefined,
      sq_footage: form.sq_footage ? parseInt(form.sq_footage) : undefined,
      parking_stall: form.parking_stall || undefined,
      locker_number: form.locker_number || undefined,
      notes: form.notes || undefined,
    };
    if (editUnit) {
      updateUnit.mutate({ id: editUnit.id, ...payload }, {
        onSuccess: () => { setOpen(false); setEditUnit(null); resetForm(); },
      });
      return;
    }
    createUnit.mutate(payload, {
      onSuccess: () => { setOpen(false); resetForm(); },
    });
  };

  if (error) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-sm text-destructive">Failed to load units. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{isIndividual ? "My Properties" : "Unit Management"}</h1>
          <p className="text-sm text-muted-foreground">{isIndividual ? `${totalUnits} registered ${totalUnits === 1 ? "property" : "properties"}` : `${totalUnits} total units, ${strApproved} STR approved`}</p>
        </div>
        <Button size="sm" onClick={() => { setEditUnit(null); resetForm(); setOpen(true); }}><Plus className="mr-1 h-4 w-4" /> {isIndividual ? "Add Property" : "Add Unit"}</Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {isLoading ? (
          <>{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</>
        ) : (
          <>
            <StatCard title={isIndividual ? "My Properties" : "Total Units"} value={totalUnits} icon={PhBuildings} iconWeight="duotone" variant="info" />
            <StatCard title="STR Approved" value={strApproved} subtitle={`${totalUnits > 0 ? ((strApproved / totalUnits) * 100).toFixed(1) : 0}% of total`} icon={PhBuildings} iconWeight="duotone" variant="success" />
            <StatCard title="Active" value={activeUnits} icon={PhBuildings} iconWeight="duotone" variant="default" />
          </>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search by unit number or owner..." className="pl-9 h-9" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No units found</p>
              <p className="text-xs text-muted-foreground">{search ? "Try adjusting your search." : "Add your first unit to get started."}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Unit</TableHead>
                  <TableHead className="text-xs">Owner</TableHead>
                  <TableHead className="text-xs">Floor / Beds</TableHead>
                  <TableHead className="text-xs">STR Approved</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u: any) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium text-sm">{u.unit_number}</TableCell>
                    <TableCell className="text-xs">
                      <div>{u.owner_name}</div>
                      {u.owner_phone && <div className="text-muted-foreground">{u.owner_phone}</div>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {u.floor_number ? `Floor ${u.floor_number}` : "—"}
                      {u.bedrooms ? ` · ${u.bedrooms}bd` : ""}
                      {u.bathrooms ? ` ${u.bathrooms}ba` : ""}
                    </TableCell>
                    <TableCell>
                      {u.str_approved ? (
                        <div>
                          <span className="text-success text-xs font-medium">Yes</span>
                          {u.strata_hotel_category && (
                            <span className="ml-1.5 text-[10px] text-muted-foreground bg-muted rounded px-1 py-0.5">
                              {u.strata_hotel_category === "category_1" ? "Cat 1" :
                               u.strata_hotel_category === "category_2" ? "Cat 2" : "Cat 3"}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">No</span>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={u.status || "inactive"} /></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-0.5">
                        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="View unit" onClick={() => setViewUnit(u)}><Eye className="h-3 w-3" aria-hidden="true" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" aria-label="Edit unit" onClick={() => openEdit(u)}><Pencil className="h-3 w-3" aria-hidden="true" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" aria-label="Delete unit" onClick={() => setDeleteUnitId(u.id)}><Trash2 className="h-3 w-3" aria-hidden="true" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Unit Dialog */}
      <Dialog open={!!viewUnit} onOpenChange={(v) => { if (!v) setViewUnit(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Unit {viewUnit?.unit_number} — Details</DialogTitle>
          </DialogHeader>
          {viewUnit && (
            <div className="space-y-4 py-2 text-sm">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div>
                  <p className="text-xs text-muted-foreground">Owner</p>
                  <p className="font-medium">{viewUnit.owner_name || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <StatusBadge status={viewUnit.status || "inactive"} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p>{viewUnit.owner_email || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p>{viewUnit.owner_phone || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">STR Approved</p>
                  <p>{viewUnit.str_approved ? "Yes" : "No"}</p>
                </div>
                {viewUnit.strata_hotel_category && (
                  <div>
                    <p className="text-xs text-muted-foreground">Hotel Category</p>
                    <Badge variant="secondary" className="text-xs">
                      {viewUnit.strata_hotel_category === "category_1" ? "Cat 1" :
                       viewUnit.strata_hotel_category === "category_2" ? "Cat 2" : "Cat 3"}
                    </Badge>
                  </div>
                )}
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                {viewUnit.floor_number != null && <div><span className="text-muted-foreground">Floor: </span>{viewUnit.floor_number}</div>}
                {viewUnit.bedrooms != null && <div><span className="text-muted-foreground">Bedrooms: </span>{viewUnit.bedrooms}</div>}
                {viewUnit.bathrooms != null && <div><span className="text-muted-foreground">Bathrooms: </span>{viewUnit.bathrooms}</div>}
                {viewUnit.sq_footage != null && <div><span className="text-muted-foreground">Sq Ft: </span>{viewUnit.sq_footage}</div>}
                {viewUnit.parking_stall && <div><span className="text-muted-foreground">Parking: </span>{viewUnit.parking_stall}</div>}
                {viewUnit.locker_number && <div><span className="text-muted-foreground">Locker: </span>{viewUnit.locker_number}</div>}
              </div>
              {viewUnit.notes && (
                <>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p className="text-xs">{viewUnit.notes}</p>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewUnit(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteUnitId} onOpenChange={(v) => { if (!v) setDeleteUnitId(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Unit?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            This will permanently remove the unit and all its associated data. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUnitId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteUnit.isPending}
              onClick={() => {
                if (!deleteUnitId) return;
                deleteUnit.mutate(deleteUnitId, { onSuccess: () => setDeleteUnitId(null) });
              }}
            >
              {deleteUnit.isPending ? "Deleting…" : "Delete Unit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Unit Dialog */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setEditUnit(null); resetForm(); } else setOpen(true); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editUnit ? `Edit Unit ${editUnit.unit_number}` : "Add New Unit"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">

            {/* ── Required: Identifiers ── */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="unit_number">Unit Number *</Label>
                <Input id="unit_number" placeholder="e.g. 101" value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ── Required: Owner ── */}
            <div className="space-y-2">
              <Label htmlFor="owner_name">Owner Name *</Label>
              <Input id="owner_name" placeholder="Full name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="owner_email">Owner Email</Label>
                <Input id="owner_email" type="email" placeholder="email@example.com" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner_phone">Owner Phone</Label>
                <Input id="owner_phone" type="tel" placeholder="+1 604-555-0100" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} />
              </div>
            </div>

            {/* ── STR Approval ── */}
            <Separator />
            <div className="flex items-center justify-between">
              <Label htmlFor="str_approved">STR Approved</Label>
              <Switch id="str_approved" checked={form.str_approved} onCheckedChange={(v) => setForm({ ...form, str_approved: v, strata_hotel_category: v ? form.strata_hotel_category : "" })} />
            </div>
            {form.str_approved && (
              <div className="space-y-2">
                <Label>Strata Hotel Category</Label>
                <Select
                  value={form.strata_hotel_category || "none"}
                  onValueChange={(v) => setForm({ ...form, strata_hotel_category: v === "none" ? "" : v as any })}
                >
                  <SelectTrigger><SelectValue placeholder="Select if applicable" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not a strata hotel unit</SelectItem>
                    <SelectItem value="category_1">Category 1 — Front desk + housekeeping + platform (pre-Dec 8 2023)</SelectItem>
                    <SelectItem value="category_2">Category 2 — 2+ strata lots restricted from PR use (pre-Dec 8 2023)</SelectItem>
                    <SelectItem value="category_3">Category 3 — New strata hotel (on/after Dec 8 2023)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Per applicable STR policy guidance — may be required for renewal documentation.
                </p>
              </div>
            )}

            {/* ── Optional: Property Details ── */}
            <Separator />
            <Collapsible open={showOptional} onOpenChange={setShowOptional}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between text-xs text-muted-foreground hover:text-foreground">
                  Property Details (optional)
                  {showOptional ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="floor_number" className="text-xs">Floor Number</Label>
                    <Input id="floor_number" type="number" min="0" placeholder="e.g. 3" value={form.floor_number} onChange={(e) => setForm({ ...form, floor_number: e.target.value })} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sq_footage" className="text-xs">Square Footage (sq ft)</Label>
                    <Input id="sq_footage" type="number" min="0" placeholder="e.g. 850" value={form.sq_footage} onChange={(e) => setForm({ ...form, sq_footage: e.target.value })} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bedrooms" className="text-xs">Bedrooms</Label>
                    <Input id="bedrooms" type="number" min="0" placeholder="e.g. 2" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bathrooms" className="text-xs">Bathrooms</Label>
                    <Input id="bathrooms" type="number" min="0" step="0.5" placeholder="e.g. 1.5" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="parking_stall" className="text-xs">Parking Stall #</Label>
                    <Input id="parking_stall" placeholder="e.g. P1-14" value={form.parking_stall} onChange={(e) => setForm({ ...form, parking_stall: e.target.value })} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="locker_number" className="text-xs">Locker / Storage #</Label>
                    <Input id="locker_number" placeholder="e.g. L2-08" value={form.locker_number} onChange={(e) => setForm({ ...form, locker_number: e.target.value })} className="h-9" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-xs">Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Any additional notes about this unit..."
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="text-xs"
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setEditUnit(null); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={(editUnit ? updateUnit.isPending : createUnit.isPending) || !form.unit_number || !form.owner_name}>
              {editUnit
                ? (updateUnit.isPending ? "Saving…" : "Save Changes")
                : (createUnit.isPending ? "Adding..." : "Add Unit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
